import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Github, Linkedin, Mail, BookOpen } from "lucide-react";
import { PROFILE } from "../data/portfolio";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || "New portfolio contact",
        message: form.message.trim(),
      });
      if (data.status === "queued") {
        toast.success("Message received. I'll get back to you soon.");
      } else {
        toast.success(data.message || "Message sent!");
      }
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send message. Try emailing me directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-white/10 py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-y-10 gap-x-0 lg:gap-10">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <p className="label-eyebrow">05 / Contact</p>
              <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                Let&apos;s build
                <br />
                <span className="text-white/40">something.</span>
              </h2>
            </Reveal>
            <Reveal delay={150}>
              <p className="mt-8 text-base text-white/70 leading-relaxed max-w-md">
                Got a role, a freelance gig, or an AI idea you want to ship?
                Drop a note — I read every message.
              </p>
            </Reveal>

            <div className="mt-10 space-y-4">
              <a
                data-testid="contact-link-email"
                href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(PROFILE.email)}&su=${encodeURIComponent("Let's connect — from your portfolio")}`}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between border-t border-white/10 py-4 hover:border-white/40 transition-colors"
              >
                <span className="flex items-center gap-3 text-sm text-white/80">
                  <Mail size={16} className="text-white/50" />
                  {PROFILE.email}
                </span>
                <ArrowUpRight size={16} className="text-white/40 group-hover:text-white transition-colors" />
              </a>
              <a
                data-testid="contact-link-linkedin"
                href={PROFILE.linkedin}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between border-t border-white/10 py-4 hover:border-white/40 transition-colors"
              >
                <span className="flex items-center gap-3 text-sm text-white/80">
                  <Linkedin size={16} className="text-white/50" />
                  LinkedIn
                </span>
                <ArrowUpRight size={16} className="text-white/40 group-hover:text-white transition-colors" />
              </a>
              <a
                data-testid="contact-link-github"
                href={PROFILE.github}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between border-t border-white/10 py-4 hover:border-white/40 transition-colors"
              >
                <span className="flex items-center gap-3 text-sm text-white/80">
                  <Github size={16} className="text-white/50" />
                  GitHub
                </span>
                <ArrowUpRight size={16} className="text-white/40 group-hover:text-white transition-colors" />
              </a>
              <a
                data-testid="contact-link-medium"
                href={PROFILE.medium}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between border-t border-b border-white/10 py-4 hover:border-white/40 transition-colors"
              >
                <span className="flex items-center gap-3 text-sm text-white/80">
                  <BookOpen size={16} className="text-white/50" />
                  Medium
                </span>
                <ArrowUpRight size={16} className="text-white/40 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          <Reveal delay={200} className="col-span-12 lg:col-span-7 lg:pl-8 lg:border-l lg:border-white/10">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="label-eyebrow">Name *</label>
                  <input
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="mt-3 w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder:text-neutral-700 focus:outline-none focus:border-white transition-colors font-mono-ui text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="label-eyebrow">Email *</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className="mt-3 w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder:text-neutral-700 focus:outline-none focus:border-white transition-colors font-mono-ui text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label-eyebrow">Subject</label>
                <input
                  data-testid="contact-input-subject"
                  type="text"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="What's this about?"
                  className="mt-3 w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder:text-neutral-700 focus:outline-none focus:border-white transition-colors font-mono-ui text-sm"
                />
              </div>

              <div>
                <label className="label-eyebrow">Message *</label>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell me about the role, the project, or the problem you're trying to solve."
                  rows={5}
                  className="mt-3 w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder:text-neutral-700 focus:outline-none focus:border-white transition-colors font-mono-ui text-sm resize-none"
                  required
                />
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-white text-black px-8 py-4 text-xs font-mono uppercase tracking-[0.2em] hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? "Sending..." : "Send message"}
                <ArrowUpRight size={16} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
